import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '../contexts/UserDataContext';

const BullPin = () => {
  const { user } = useAuth();
  const { userApiKeys } = useUserData();
  const [activeTab, setActiveTab] = useState('overview');
  const [tradingMode, setTradingMode] = useState('STOCKS'); // 'STOCKS' or 'CRYPTO'
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [backtestResults, setBacktestResults] = useState(null);
  const [paperTrades, setPaperTrades] = useState([]);
  
  // Stocks portfolio
  const [stocksPortfolio, setStocksPortfolio] = useState({
    totalValue: 12750,
    cash: 2500,
    positions: [
      { symbol: 'AAPL', shares: 10, avgPrice: 245.50, currentPrice: 258.45, pnl: 129.50 },
      { symbol: 'MSFT', shares: 5, avgPrice: 480.20, currentPrice: 520.54, pnl: 201.70 },
      { symbol: 'GOOGL', shares: 8, avgPrice: 235.80, currentPrice: 251.69, pnl: 127.12 },
      { symbol: 'TSLA', shares: 3, avgPrice: 420.15, currentPrice: 438.97, pnl: 56.46 }
    ],
    pnl: 514.78,
    pnlPercent: 4.22,
    initialValue: 10000
  });

  // Crypto portfolio
  const [cryptoPortfolio, setCryptoPortfolio] = useState({
    totalValue: 15750,
    cash: 5000, // USDT
    positions: [
      { symbol: 'BTCUSDT', qty: 0.15, avgPrice: 45000, currentPrice: 52000, pnl: 1050 },
      { symbol: 'ETHUSDT', qty: 2.5, avgPrice: 3200, currentPrice: 3800, pnl: 1500 },
      { symbol: 'ADAUSDT', qty: 1000, avgPrice: 0.45, currentPrice: 0.52, pnl: 70 }
    ],
    pnl: 2620,
    pnlPercent: 19.9,
    initialValue: 10000,
    currencies: [
      { currency: 'USDT', balance: 5000, available: 5000, locked: 0 },
      { currency: 'BTC', balance: 0.15, available: 0.15, locked: 0 },
      { currency: 'ETH', balance: 2.5, available: 2.5, locked: 0 }
    ]
  });

  // Get current portfolio based on trading mode
  const portfolio = tradingMode === 'CRYPTO' ? cryptoPortfolio : stocksPortfolio;

  // Sample strategies data
  useEffect(() => {
    const stocksStrategies = [
      {
        id: 1,
        name: 'Moving Average Crossover',
        description: 'Buy when short MA crosses above long MA, sell when it crosses below',
        type: 'trend_following',
        status: 'active',
        tradingMode: 'STOCKS',
        performance: {
          totalReturn: 15.2,
          sharpeRatio: 1.8,
          maxDrawdown: -8.5,
          winRate: 65.4
        }
      },
      {
        id: 2,
        name: 'RSI Mean Reversion',
        description: 'Buy oversold (RSI < 30), sell overbought (RSI > 70)',
        type: 'mean_reversion',
        status: 'backtesting',
        tradingMode: 'STOCKS',
        performance: {
          totalReturn: 8.7,
          sharpeRatio: 1.2,
          maxDrawdown: -12.3,
          winRate: 58.9
        }
      },
      {
        id: 3,
        name: 'AI Sentiment Strategy',
        description: 'AI-powered strategy using news sentiment and social media data',
        type: 'ai_driven',
        status: 'development',
        tradingMode: 'STOCKS',
        performance: {
          totalReturn: 22.1,
          sharpeRatio: 2.1,
          maxDrawdown: -6.8,
          winRate: 71.2
        }
      }
    ];

    const cryptoStrategies = [
      {
        id: 4,
        name: 'Dollar Cost Averaging',
        description: 'Automatically buy fixed amount at regular intervals',
        type: 'dca',
        status: 'active',
        tradingMode: 'CRYPTO',
        performance: {
          totalReturn: 18.5,
          sharpeRatio: 1.5,
          maxDrawdown: -15.2,
          winRate: 72.1
        }
      },
      {
        id: 5,
        name: 'Grid Trading',
        description: 'Place buy/sell orders at predetermined price levels',
        type: 'grid_trading',
        status: 'active',
        tradingMode: 'CRYPTO',
        performance: {
          totalReturn: 25.3,
          sharpeRatio: 2.2,
          maxDrawdown: -8.7,
          winRate: 68.4
        }
      },
      {
        id: 6,
        name: 'Arbitrage Bot',
        description: 'Exploit price differences across exchanges',
        type: 'arbitrage',
        status: 'development',
        tradingMode: 'CRYPTO',
        performance: {
          totalReturn: 12.8,
          sharpeRatio: 3.1,
          maxDrawdown: -2.1,
          winRate: 85.6
        }
      }
    ];

    const allStrategies = [...stocksStrategies, ...cryptoStrategies];
    setStrategies(allStrategies);
  }, []);

  const renderOverview = () => (
    <div className="bull-pin-overview">
      <div className="welcome-section">
        <h2>Welcome to Bull Pen</h2>
        <p>Your AI-powered algorithmic trading platform</p>
      </div>

      {/* Trading Mode Selector */}
      <div className="trading-mode-selector">
        <h3>Choose Your Trading Mode</h3>
        <div className="mode-tiles">
          <div 
            className={`mode-tile ${tradingMode === 'STOCKS' ? 'active' : ''}`}
            onClick={() => setTradingMode('STOCKS')}
          >
            <div className="mode-icon">📈</div>
            <h4>Stocks & Options</h4>
            <p>Trade traditional equities with advanced strategies</p>
            <div className="mode-stats">
              <span className="stat">${stocksPortfolio.totalValue.toLocaleString()}</span>
              <span className="stat-label">Portfolio Value</span>
            </div>
          </div>
          
          <div 
            className={`mode-tile ${tradingMode === 'CRYPTO' ? 'active' : ''}`}
            onClick={() => setTradingMode('CRYPTO')}
          >
            <div className="mode-icon">₿</div>
            <h4>Crypto Trading</h4>
            <p>Algorithmic cryptocurrency trading with DCA, Grid, and Arbitrage</p>
            <div className="mode-stats">
              <span className="stat">${cryptoPortfolio.totalValue.toLocaleString()}</span>
              <span className="stat-label">Portfolio Value</span>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3>{tradingMode === 'CRYPTO' ? 'Crypto Portfolio' : 'Paper Trading Portfolio'}</h3>
          <div className="metric-large">${portfolio.totalValue.toLocaleString()}</div>
          <div className={`metric-change ${portfolio.pnl >= 0 ? 'positive' : 'negative'}`}>
            {portfolio.pnl >= 0 ? '+' : ''}${portfolio.pnl.toFixed(2)} ({portfolio.pnlPercent.toFixed(2)}%)
          </div>
          <div className="metric-subtitle">Started with ${portfolio.initialValue.toLocaleString()}</div>
        </div>

        <div className="dashboard-card">
          <h3>Active Strategies</h3>
          <div className="metric-large">{strategies.filter(s => s.status === 'active' && s.tradingMode === tradingMode).length}</div>
          <div className="metric-subtitle">{tradingMode === 'CRYPTO' ? 'Crypto Strategies' : 'Stock Strategies'}</div>
        </div>

        <div className="dashboard-card">
          <h3>{tradingMode === 'CRYPTO' ? 'Crypto Balances' : 'Paper Trades'}</h3>
          <div className="metric-large">
            {tradingMode === 'CRYPTO' 
              ? `${portfolio.currencies?.length || 0} currencies`
              : paperTrades.length
            }
          </div>
          <div className="metric-subtitle">
            {tradingMode === 'CRYPTO' 
              ? `USDT: $${portfolio.cash.toLocaleString()}`
              : 'Today'
            }
          </div>
        </div>

        <div className="dashboard-card">
          <h3>Best Performer</h3>
          <div className="metric-large">
            {tradingMode === 'CRYPTO' 
              ? 'Grid Trading'
              : 'AI Sentiment'
            }
          </div>
          <div className="metric-change positive">
            +{tradingMode === 'CRYPTO' 
              ? strategies.find(s => s.id === 5)?.performance.totalReturn
              : strategies.find(s => s.id === 3)?.performance.totalReturn
            }% Return
          </div>
          <div className="metric-subtitle">Strategy</div>
        </div>
      </div>

      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="action-buttons">
          <button className="action-btn primary" onClick={() => setActiveTab('strategies')}>
            Create Strategy
          </button>
          <button className="action-btn secondary" onClick={() => setActiveTab('backtesting')}>
            Run Backtest
          </button>
          <button className="action-btn secondary" onClick={() => setActiveTab('paper-trading')}>
            Start Paper Trading
          </button>
        </div>
      </div>
    </div>
  );

  const renderStrategies = () => {
    const filteredStrategies = strategies.filter(s => s.tradingMode === tradingMode);
    
    return (
      <div className="strategies-section">
        <div className="section-header">
          <h2>{tradingMode === 'CRYPTO' ? 'Crypto Trading Strategies' : 'Stock Trading Strategies'}</h2>
          <button className="btn-primary">Create New Strategy</button>
        </div>

        <div className="strategy-filters">
          <button className={`filter-btn ${activeTab === 'strategies' ? 'active' : ''}`}>
            All {tradingMode === 'CRYPTO' ? 'Crypto' : 'Stock'} Strategies
          </button>
          <button className="filter-btn">Active</button>
          <button className="filter-btn">Backtesting</button>
          <button className="filter-btn">Development</button>
        </div>

        <div className="strategies-grid">
          {filteredStrategies.map(strategy => (
          <div key={strategy.id} className="strategy-card">
            <div className="strategy-header">
              <h3>{strategy.name}</h3>
              <span className={`status-badge ${strategy.status}`}>
                {strategy.status.charAt(0).toUpperCase() + strategy.status.slice(1)}
              </span>
            </div>
            <p className="strategy-description">{strategy.description}</p>
            <div className="strategy-metrics">
              <div className="metric">
                <span className="metric-label">Total Return</span>
                <span className={`metric-value ${strategy.performance.totalReturn >= 0 ? 'positive' : 'negative'}`}>
                  {strategy.performance.totalReturn >= 0 ? '+' : ''}{strategy.performance.totalReturn}%
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Sharpe Ratio</span>
                <span className="metric-value">{strategy.performance.sharpeRatio}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Win Rate</span>
                <span className="metric-value">{strategy.performance.winRate}%</span>
              </div>
            </div>
            <div className="strategy-actions">
              <button className="btn-secondary">Edit</button>
              <button className="btn-primary">Deploy</button>
            </div>
          </div>
        ))}
      </div>
    </div>
    );
  };

  const renderBacktesting = () => (
    <div className="backtesting-section">
      <div className="section-header">
        <h2>Strategy Backtesting</h2>
        <p>Test your strategies against historical data</p>
      </div>

      <div className="backtest-controls">
        <div className="control-group">
          <label>Strategy</label>
          <select>
            <option>Select Strategy</option>
            {strategies.map(strategy => (
              <option key={strategy.id} value={strategy.id}>{strategy.name}</option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label>Time Period</label>
          <select>
            <option value="1y">1 Year</option>
            <option value="2y">2 Years</option>
            <option value="5y">5 Years</option>
            <option value="10y">10 Years</option>
          </select>
        </div>
        <div className="control-group">
          <label>Initial Capital</label>
          <input type="number" defaultValue="10000" />
        </div>
        <button className="btn-primary">Run Backtest</button>
      </div>

      {backtestResults && (
        <div className="backtest-results">
          <h3>Backtest Results</h3>
          <div className="results-grid">
            <div className="result-card">
              <h4>Total Return</h4>
              <div className="result-value positive">+15.2%</div>
            </div>
            <div className="result-card">
              <h4>Sharpe Ratio</h4>
              <div className="result-value">1.8</div>
            </div>
            <div className="result-card">
              <h4>Max Drawdown</h4>
              <div className="result-value negative">-8.5%</div>
            </div>
            <div className="result-card">
              <h4>Win Rate</h4>
              <div className="result-value">65.4%</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPaperTrading = () => (
    <div className="paper-trading-section">
      <div className="section-header">
        <h2>{tradingMode === 'CRYPTO' ? 'Crypto Trading' : 'Paper Trading'}</h2>
        <p>{tradingMode === 'CRYPTO' ? 'Trade cryptocurrencies with virtual money' : 'Practice trading with virtual money'}</p>
      </div>

      <div className="paper-trading-dashboard">
        <div className="trading-controls">
          <div className="control-group">
            <label>Symbol</label>
            <input 
              type="text" 
              placeholder={tradingMode === 'CRYPTO' ? "e.g., BTCUSDT" : "e.g., AAPL"} 
            />
          </div>
          <div className="control-group">
            <label>{tradingMode === 'CRYPTO' ? 'Quantity' : 'Quantity'}</label>
            <input type="number" placeholder={tradingMode === 'CRYPTO' ? "0.1" : "100"} />
          </div>
          <div className="control-group">
            <label>Order Type</label>
            <select>
              <option>Market</option>
              <option>Limit</option>
              <option>Stop</option>
              {tradingMode === 'CRYPTO' && <option>OCO</option>}
            </select>
          </div>
          <div className="control-group">
            <label>Side</label>
            <select>
              <option>Buy</option>
              <option>Sell</option>
            </select>
          </div>
          <button className="btn-primary">Place Order</button>
        </div>

        <div className="positions-table">
          <h3>Current Positions</h3>
          <table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>{tradingMode === 'CRYPTO' ? 'Quantity' : 'Shares'}</th>
                <th>Avg Price</th>
                <th>Current Price</th>
                <th>P&L</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {portfolio.positions.map((position, index) => (
                <tr key={index}>
                  <td>{position.symbol}</td>
                  <td>{tradingMode === 'CRYPTO' ? position.qty?.toFixed(6) : position.shares}</td>
                  <td>${position.avgPrice.toFixed(2)}</td>
                  <td>${position.currentPrice.toFixed(2)}</td>
                  <td className={position.pnl >= 0 ? 'positive' : 'negative'}>
                    {position.pnl >= 0 ? '+' : ''}${position.pnl.toFixed(2)}
                  </td>
                  <td>
                    <button className="btn-small">Close</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {tradingMode === 'CRYPTO' && portfolio.currencies && (
          <div className="crypto-balances">
            <h3>Crypto Balances</h3>
            <div className="balances-grid">
              {portfolio.currencies.map((currency, index) => (
                <div key={index} className="balance-card">
                  <div className="currency-name">{currency.currency}</div>
                  <div className="currency-balance">{currency.balance.toFixed(6)}</div>
                  <div className="currency-available">Available: {currency.available.toFixed(6)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderTabs = () => (
    <div className="bull-pin-tabs">
      <button 
        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
        onClick={() => setActiveTab('overview')}
      >
        Overview
      </button>
      <button 
        className={`tab-btn ${activeTab === 'strategies' ? 'active' : ''}`}
        onClick={() => setActiveTab('strategies')}
      >
        Strategies
      </button>
      <button 
        className={`tab-btn ${activeTab === 'backtesting' ? 'active' : ''}`}
        onClick={() => setActiveTab('backtesting')}
      >
        Backtesting
      </button>
      <button 
        className={`tab-btn ${activeTab === 'paper-trading' ? 'active' : ''}`}
        onClick={() => setActiveTab('paper-trading')}
      >
        Paper Trading
      </button>
    </div>
  );

  return (
    <div className="bull-pin-page">
      <div className="page-header">
        <div className="header-content">
          <div className="logo-section">
            <img src={require('../images/bullpin.png')} alt="Bull Pen Logo" className="page-logo" />
          </div>
          <div className="title-section">
            <h1>Bull Pen</h1>
            <p>AI-Powered Algorithmic Trading Platform</p>
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-value">${portfolio.totalValue.toLocaleString()}</span>
                <span className="stat-label">Paper Trading</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{strategies.filter(s => s.status === 'active').length}</span>
                <span className="stat-label">Active Strategies</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">+{portfolio.pnlPercent.toFixed(1)}%</span>
                <span className="stat-label">Total Return</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {renderTabs()}

      <div className="tab-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'strategies' && renderStrategies()}
        {activeTab === 'backtesting' && renderBacktesting()}
        {activeTab === 'paper-trading' && renderPaperTrading()}
      </div>
    </div>
  );
};

export default BullPin;
